import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import TabBar from './components/TabBar.jsx';
import Home from './screens/Home.jsx';
import Plans from './screens/Plans.jsx';
import Lessons from './screens/Lessons.jsx';
import You from './screens/You.jsx';
import SignIn from './screens/SignIn.jsx';
import AuthCallback from './screens/AuthCallback.jsx';
import Logistics from './tools/Logistics.jsx';
import Objective from './tools/Objective.jsx';
import Draft from './tools/Draft.jsx';
import Builder from './tools/Builder.jsx';
import { useAuth } from './contexts/AuthContext.jsx';
import { PresentationProvider } from './contexts/PresentationContext.jsx';

/**
 * App — top-level routing shell.
 *
 * Auth gate behavior:
 *   - While the initial auth check is in flight, we render a quiet
 *     loading screen instead of flashing the sign-in form at users
 *     who are already authenticated.
 *   - Unauthenticated users only see /signin and /auth/callback.
 *     Any other path bounces to /signin.
 *   - Authenticated users see the full app, wrapped in
 *     PresentationProvider so every tool reads/writes the same
 *     Supabase-backed state.
 *
 * The bottom tab bar is hidden on the sign-in and callback screens
 * because the user can't usefully navigate elsewhere there.
 *
 * Tool flow after the four-step refactor:
 *   Logistics → Objective → Draft → Builder
 * Old tool paths redirect to their new home so existing links/bookmarks
 * don't 404 (the Netlify _redirects file covers hard reloads / direct
 * hits; these client-side routes cover in-app navigation):
 *   /tools/pages      → /tools/logistics   (Pages folded into Logistics)
 *   /tools/brainstorm → /tools/draft        (merged into Draft)
 *   /tools/design     → /tools/draft        (merged into Draft)
 */
export default function App() {
  const location = useLocation();
  const { user, loading } = useAuth();

  // Brief loading state during the initial session check. Without
  // this, a returning user would see /signin flash before being
  // recognized.
  if (loading) {
    return (
      <div className="app-shell">
        <main className="app-main" />
      </div>
    );
  }

  // ── Public routes (unauthenticated) ──
  if (!user) {
    return (
      <div className="app-shell">
        <main className="app-main">
          <Routes>
            <Route path="/signin" element={<SignIn />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            {/* Any other path → sign-in */}
            <Route path="*" element={<Navigate to="/signin" replace />} />
          </Routes>
        </main>
      </div>
    );
  }

  // ── Authenticated routes ──
  // Once signed in, /signin redirects home so the user doesn't get
  // stuck looking at the sign-in screen.
  const hideTabBar = location.pathname === '/auth/callback';

  return (
    <PresentationProvider>
      <div className="app-shell">
        <main className="app-main">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/lessons" element={<Lessons />} />
            <Route path="/you" element={<You />} />
            <Route path="/signin" element={<Navigate to="/" replace />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Tools */}
            <Route path="/tools/logistics" element={<Logistics />} />
            <Route path="/tools/objective"  element={<Objective />} />
            <Route path="/tools/draft"      element={<Draft />} />
            <Route path="/tools/builder"    element={<Builder />} />

            {/* Redirects for retired tool paths */}
            <Route path="/tools/pages"      element={<Navigate to="/tools/logistics" replace />} />
            <Route path="/tools/brainstorm" element={<Navigate to="/tools/draft" replace />} />
            <Route path="/tools/design"     element={<Navigate to="/tools/draft" replace />} />

            {/* Fallback for anything else */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        {!hideTabBar && <TabBar />}
      </div>
    </PresentationProvider>
  );
}
